import HeroBanner from '../../../features/chat/HeroBanner/HeroBanner';
import ChatWindow from '../../../features/chat/ChatWindow/ChatWindow';
import HeroBannerSkeleton from '../../../shared/HeroBannerSkeleton';
import {
  Box,
  Paper,
  useTheme,
  useMediaQuery,
  Alert,
  Fade,
} from '@mui/material';
import axios from '../../../../api/myAxios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PreviewWindowProps, HeroBannerEditableData } from '../../../../types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useHeroBannerData } from '../../../../hooks/useHeroBannerData';

const PreviewWindow: React.FC<PreviewWindowProps> = ({
  userPhoto,
  selectedWorkspace,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const { heroBannerEditableData, setHeroBannerEditableData, isDataLoaded } =
    useHeroBannerData(selectedWorkspace?.heroBannerData);

  const saveHeroBannerMutation = useMutation({
    mutationFn: async ({
      id,
      updatedData,
    }: {
      id: string;
      updatedData: HeroBannerEditableData;
    }) => {
      const response = await axios.put(`/api/workspaces/${id}`, {
        name: selectedWorkspace.name,
        description: selectedWorkspace.description,
        heroBannerData: updatedData,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      setShowSuccessMessage(true);
    },
    onError: (error) => {
      console.error(t('error.console.errorSaving'), error);
      setShowErrorMessage(true);
    },
  });

  useEffect(() => {
    if (showSuccessMessage) {
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage]);

  useEffect(() => {
    if (showErrorMessage) {
      const timer = setTimeout(() => {
        setShowErrorMessage(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showErrorMessage]);

  const handleSaveHeroBanner = (
    id: string,
    updatedData: HeroBannerEditableData
  ) => {
    saveHeroBannerMutation.mutate({ id, updatedData });
  };

  return (
    <Paper
      elevation={isMobile ? 1 : 3}
      sx={{
        borderRadius: isMobile ? 1 : 2,
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%',
        mx: isMobile ? 0 : 'auto',
        my: isMobile ? 0 : 2,
      }}
    >
      {!isMobile && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: isMobile ? 1 : 2,
            py: isMobile ? 0.5 : 1,
            bgcolor: '#f0f0f0',
            borderBottom: '1px solid #d0d0d0',
            minHeight: isMobile ? 32 : 40,
          }}
        >
          <Box
            sx={{
              width: isMobile ? 8 : 12,
              height: isMobile ? 8 : 12,
              borderRadius: '50%',
              bgcolor: '#ff5f57',
            }}
          />
          <Box
            sx={{
              width: isMobile ? 8 : 12,
              height: isMobile ? 8 : 12,
              borderRadius: '50%',
              bgcolor: '#ffbd2e',
            }}
          />
          <Box
            sx={{
              width: isMobile ? 8 : 12,
              height: isMobile ? 8 : 12,
              borderRadius: '50%',
              bgcolor: '#28c840',
            }}
          />
        </Box>
      )}
      <Box
        sx={{
          p: isMobile ? 1 : 3,
          display: 'flex',
          width: '100%',
          flexDirection: 'column',
          gap: isMobile ? 1 : 2,
          minHeight: isMobile ? 'calc(100vh - 100px)' : 'auto',
        }}
      >
        <Box
          sx={{
            width: '100%',
            overflow: 'hidden',
          }}
        >
          {isDataLoaded ? (
            <HeroBanner
              userPhoto={userPhoto}
              workspaceId={selectedWorkspace.id}
              previewMode={true}
              heroBannerEditableData={heroBannerEditableData || undefined}
              setHeroBannerEditableData={setHeroBannerEditableData}
              onSave={() => {
                heroBannerEditableData &&
                  handleSaveHeroBanner(
                    selectedWorkspace.id,
                    heroBannerEditableData
                  );
              }}
            />
          ) : (
            <HeroBannerSkeleton variant="default" />
          )}

          {showSuccessMessage && (
            <Fade in={true} timeout={1000}>
              <Alert severity="success" sx={{ width: '100%', mb: 3 }}>
                {t('chatWindow.saveHeroBannerSuccess')}
              </Alert>
            </Fade>
          )}

          {showErrorMessage && (
            <Fade in={true} timeout={1000}>
              <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
                {t('chatWindow.saveHeroBannerError')}
              </Alert>
            </Fade>
          )}
        </Box>

        <Box
          sx={{
            width: '100%',
            minHeight: isMobile ? '500px' : '500px',
            maxHeight: isMobile ? '80vh' : '70vh',
            maxWidth: isMobile ? '100%' : '1200px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            mx: isMobile ? 0 : 'auto',
          }}
        >
          <ChatWindow
            subdomain={selectedWorkspace.subdomain}
            userPhoto={userPhoto}
            workspaceId={selectedWorkspace.id}
            isPreviewMode={true}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default PreviewWindow;
